/**
 * BulkIndexEditor - Bulk edit Index metadata across multiple indices
 *
 * Similar workflow to BulkVersionEditor, but operates on Index records
 * (the text metadata) rather than Version records (text content/translations).
 *
 * Workflow:
 * 1. User enters a versionTitle to find indices that have matching versions
 * 2. User selects which indices to update
 * 3. User fills in index metadata fields
 * 4. On save, updates each Index via the raw API
 *
 * Special features:
 * - Auto-detection for commentary texts ("X on Y" pattern)
 * - Author validation against AuthorTopic
 *
 * Backend API: POST /api/v2/raw/index/{title}?update=1
 *
 * Documentation:
 * - See /docs/modtools/MODTOOLS_GUIDE.md for quick reference
 * - See /docs/modtools/COMPONENT_LOGIC.md for detailed implementation logic
 * - Index fields are defined in ../constants/fieldMetadata.js
 */
import React, { useState, useEffect } from 'react';
import { getCsrfToken } from '../../sefaria/csrf';
import Sefaria from '../../sefaria/sefaria';
import { INDEX_FIELD_METADATA } from '../constants/fieldMetadata';
import ModToolsSection from './shared/ModToolsSection';
import IndexSelector from './shared/IndexSelector';
import StatusMessage from './shared/StatusMessage';

/**
 * Detailed help documentation for this tool
 */
const HELP_CONTENT = (
  <>
    <h3>What This Tool Does</h3>
    <p>
      This tool edits <strong>Index metadata</strong> (text catalog records) across multiple texts.
      An "Index" in Sefaria is the master record for a text, containing its title, category,
      authorship, composition date, and structural information.
    </p>
    <p>
      Unlike the Version Editor which edits translations/editions, this tool edits the
      underlying text record itself. Use this when you need to update catalog information
      like descriptions, categories, authors, or commentary relationships.
    </p>

    <h3>How It Works</h3>
    <ol>
      <li><strong>Search:</strong> Enter a version title to find all indices that have versions with that title.</li>
      <li><strong>Select:</strong> Choose which indices to update.</li>
      <li><strong>Edit:</strong> Fill in the metadata fields you want to change.</li>
      <li><strong>Save:</strong> Each index is updated individually via the API, with cache clearing.</li>
    </ol>

    <h3>Available Fields</h3>
    <table className="field-table">
      <thead>
        <tr><th>Field</th><th>Description</th></tr>
      </thead>
      <tbody>
        <tr><td><code>enDesc</code></td><td>English description of the text (shown in reader)</td></tr>
        <tr><td><code>heDesc</code></td><td>Hebrew description of the text</td></tr>
        <tr><td><code>enShortDesc</code></td><td>Brief English description for search results</td></tr>
        <tr><td><code>heShortDesc</code></td><td>Brief Hebrew description</td></tr>
        <tr><td><code>categories</code></td><td>Category path in the table of contents (e.g., "Mishnah, Seder Zeraim")</td></tr>
        <tr><td><code>authors</code></td><td>Author slugs (must exist in AuthorTopic). Comma-separated.</td></tr>
        <tr><td><code>compDate</code></td><td>Composition date. Single year or range like [1200, 1250]</td></tr>
        <tr><td><code>compPlace</code></td><td>Place of composition (English)</td></tr>
        <tr><td><code>pubDate</code></td><td>Publication date</td></tr>
        <tr><td><code>pubPlace</code></td><td>Place of publication (English)</td></tr>
        <tr><td><code>dependence</code></td><td>"Commentary" or "Targum" - marks text as dependent on another</td></tr>
        <tr><td><code>base_text_titles</code></td><td>For commentaries: exact titles of base texts. Comma-separated.</td></tr>
        <tr><td><code>collective_title</code></td><td>For commentaries: the commentary name (e.g., "Rashi")</td></tr>
      </tbody>
    </table>

    <h3>Auto-Detection Feature</h3>
    <p>
      For texts with "X on Y" naming pattern (e.g., "Rashi on Genesis"), you can use
      <code>'auto'</code> as a value for certain fields:
    </p>
    <ul>
      <li><code>dependence: 'auto'</code> - Sets to "Commentary" if pattern detected</li>
      <li><code>base_text_titles: 'auto'</code> - Extracts the base text name (e.g., "Genesis")</li>
      <li><code>collective_title: 'auto'</code> - Extracts the commentary name (e.g., "Rashi")</li>
      <li><code>authors: 'auto'</code> - Looks up the commentary name as an AuthorTopic</li>
    </ul>

    <div className="warning">
      <strong>Important Notes:</strong>
      <ul>
        <li><strong>Authors must exist</strong> in the AuthorTopic database. Invalid author names will fail validation.</li>
        <li><strong>Base text titles must be exact</strong> index titles (e.g., "Mishnah Berakhot", not "Mishnah").</li>
        <li><strong>Categories</strong> must match existing category paths in the Sefaria table of contents.</li>
        <li>Changes trigger a <strong>cache reset</strong> for each index, which may take a moment.</li>
        <li>Changes are applied <strong>immediately to production</strong>. There is no undo.</li>
      </ul>
    </div>

    <h3>Common Use Cases</h3>
    <ul>
      <li>Adding descriptions to a set of related texts</li>
      <li>Setting up commentary metadata for a new commentary series</li>
      <li>Moving texts to a different category</li>
      <li>Adding authorship information to texts by the same author</li>
    </ul>
  </>
);

/**
 * Detect commentary pattern from title (e.g., "Rashi on Genesis")
 * Returns { commentaryName, baseText } or null
 */
const detectCommentaryPattern = (title) => {
  const match = title.match(/^(.+?)\s+on\s+(.+)$/);
  if (match) {
    return {
      commentaryName: match[1].trim(),
      baseText: match[2].trim()
    };
  }
  return null;
};

/**
 * Check whether an exact Index with this title exists.
 * index_api is wrapped by catch_error_as_json, so an unknown title comes back as HTTP 200
 * with an {error: ...} body rather than a rejection; check the payload and let real
 * transport failures propagate.
 *
 * No caching needed here: Sefaria.getIndexDetails already goes through
 * _cachedApiPromise (store: Sefaria._indexDetails), so repeating a title across the
 * selected indices costs one request, not one per index.
 */
const indexExists = async (title) => {
  const data = await Sefaria.getIndexDetails(title);
  return !!data && !data.error;
};

/**
 * Check whether a Term exists with this name.
 *
 * No API call needed: every page is served the full Term list. The `terms_json` context
 * processor calls library.get_simple_term_mapping_json(), which is built by looping over
 * TermSet() (every Term in Mongo, keyed by name); templates/js/data.js sets it as
 * Sefaria.terms, and Sefaria.setup() copies it into _translateTerms.
 *
 * Caveat: this is a page-load snapshot, so a Term created in another tab after this page
 * loaded is not in it. The warning below tells the user to reload in that case.
 */
const termExists = (name) => name in Sefaria._translateTerms;

const BulkIndexEditor = () => {
  // Search state
  const [vtitle, setVtitle] = useState("");
  const [lang, setLang] = useState("");
  const [searched, setSearched] = useState(false);

  // Results state
  // indices: Array of {title: string, categories?: string[]} objects
  const [indices, setIndices] = useState([]);
  const [pick, setPick] = useState(new Set());
  const [categories, setCategories] = useState([]);

  // Edit state
  const [updates, setUpdates] = useState({});

  // UI state
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, label: "" });

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await Sefaria.apiRequestWithBody('/api/index', null, null, 'GET');
        const cats = [];
        const extractCategories = (node, path = []) => {
          if (node.category) {
            const fullPath = [...path, node.category].join(", ");
            cats.push(fullPath);
          }
          if (node.contents) {
            node.contents.forEach(item => {
              extractCategories(item, node.category ? [...path, node.category] : path);
            });
          }
        };
        data.forEach(cat => extractCategories(cat));
        setCategories(cats.sort());
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    loadCategories();
  }, []);

  /**
   * Clear search and reset state
   */
  const clearSearch = () => {
    setIndices([]);
    setPick(new Set());
    setUpdates({});
    setMsg("");
    setSearched(false);
  };

  /**
   * Load indices matching the version title
   */
  const load = async () => {
    if (!vtitle.trim()) {
      setMsg("Please enter a version title");
      return;
    }

    setLoading(true);
    setSearched(true);
    setMsg("Loading indices...");

    const urlParams = { versionTitle: vtitle };
    if (lang) {
      urlParams.language = lang;
    }

    try {
      const data = await Sefaria.apiRequestWithBody('/api/version-indices', urlParams, null, 'GET');
      const resultIndices = data.indices || [];
      const resultMetadata = data.metadata || {};
      // Combine indices and metadata into single array of objects
      const combinedIndices = resultIndices.map(title => ({
        title,
        categories: resultMetadata[title]?.categories
      }));
      setIndices(combinedIndices);
      setPick(new Set(resultIndices)); // Set of title strings
      if (resultIndices.length > 0) {
        setMsg(`Found ${resultIndices.length} indices with version "${vtitle}"`);
      } else {
        setMsg("");
      }
    } catch (error) {
      setMsg(`Error: ${error.message || "Failed to load indices"}`);
      setIndices([]);
      setPick(new Set());
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle field value changes
   */
  const handleFieldChange = (fieldName, value) => {
    setUpdates(prev => ({ ...prev, [fieldName]: value }));
  };

  /**
   * Save changes to selected indices
   */
  const save = async () => {
    if (!pick.size || !Object.keys(updates).length) return;

    setSaving(true);
    setMsg("Saving changes...");
    setProgress({ current: 0, total: pick.size, label: "" });

    // Process updates to ensure correct data types
    const processedUpdates = {};

    for (const [field, value] of Object.entries(updates)) {
      if (!value) continue;

      const fieldMeta = INDEX_FIELD_METADATA[field];
      if (!fieldMeta) {
        processedUpdates[field] = value;
        continue;
      }

      switch (fieldMeta.type) {
        case 'array':
          if (value === 'auto') {
            processedUpdates[field] = 'auto';
          } else {
            processedUpdates[field] = value.split(',').map(v => v.trim()).filter(v => v);
          }
          break;
        case 'daterange':
          if (value.startsWith('[') && value.endsWith(']')) {
            try {
              processedUpdates[field] = JSON.parse(value);
            } catch (e) {
              setMsg(`Invalid date format for ${field}`);
              setSaving(false);
              return;
            }
          } else {
            const year = parseInt(value);
            if (!isNaN(year)) {
              // Index._validate requires compDate/pubDate to be a list of integers
              processedUpdates[field] = [year];
            } else {
              setMsg(`Invalid date format for ${field}`);
              setSaving(false);
              return;
            }
          }
          break;
        case 'number':
          const numValue = parseInt(value);
          if (isNaN(numValue)) {
            setMsg(`Invalid number format for ${field}`);
            setSaving(false);
            return;
          }
          processedUpdates[field] = numValue;
          break;
        default:
          processedUpdates[field] = value;
      }
    }

    // Validate authors if present
    if (processedUpdates.authors && processedUpdates.authors !== 'auto') {
      try {
        const authorSlugs = [];
        for (const authorName of processedUpdates.authors) {
          const response = await Sefaria.apiRequestWithBody(`/api/name/${authorName}`, null, null, 'GET');
          const matches = response.completion_objects?.filter(t => t.type === 'AuthorTopic') || [];
          const exactMatch = matches.find(t => t.title.toLowerCase() === authorName.toLowerCase());

          if (!exactMatch) {
            const closestMatches = matches.map(t => t.title).slice(0, 3);
            const msg = matches.length > 0
              ? `Invalid author "${authorName}". Did you mean: ${closestMatches.join(', ')}?`
              : `Invalid author "${authorName}". Make sure it exists in the Authors topic.`;
            setMsg(`Error: ${msg}`);
            setSaving(false);
            return;
          }
          authorSlugs.push(exactMatch.key);
        }
        processedUpdates.authors = authorSlugs;
      } catch (e) {
        setMsg(`Error validating authors`);
        setSaving(false);
        return;
      }
    }

    let successCount = 0;
    const errors = [];
    const warnings = [];

    let processed = 0;
    for (const indexTitle of pick) {
      setProgress({ current: processed, total: pick.size, label: indexTitle });
      try {
        setMsg(`Updating ${indexTitle}...`);

        // Fetch fresh raw data rather than Sefaria.getIndexDetails, whose client-side cache
        // is never invalidated after a save: a second save in the same session would post
        // stale categories/schema and silently revert the first save's changes.
        const existingIndexData = await Sefaria.getRawIndex(indexTitle);
        if (!existingIndexData) {
          errors.push(`${indexTitle}: Could not fetch existing index data.`);
          continue;
        }

        let indexSpecificUpdates = { ...processedUpdates };
        const pattern = detectCommentaryPattern(indexTitle);

        // Handle auto-detection for various fields
        if (indexSpecificUpdates.dependence === 'auto') {
          indexSpecificUpdates.dependence = pattern ? 'Commentary' : undefined;
          if (!pattern) delete indexSpecificUpdates.dependence;
        }

        if (indexSpecificUpdates.base_text_titles === 'auto') {
          if (pattern?.baseText) {
            indexSpecificUpdates.base_text_titles = [pattern.baseText];
          } else {
            delete indexSpecificUpdates.base_text_titles;
          }
        }

        if (indexSpecificUpdates.collective_title === 'auto') {
          if (pattern?.commentaryName) {
            indexSpecificUpdates.collective_title = pattern.commentaryName;
          } else {
            delete indexSpecificUpdates.collective_title;
          }
        }

        // Handle authors auto-detection. Like the other 'auto' fields, this only
        // applies to "X on Y" titles; for any non-matching title the field is dropped
        // so the literal string 'auto' is never written to the index.
        if (indexSpecificUpdates.authors === 'auto') {
          if (pattern?.commentaryName) {
            try {
              const response = await Sefaria.apiRequestWithBody(`/api/name/${pattern.commentaryName}`, null, null, 'GET');
              const matches = response.completion_objects?.filter(t => t.type === 'AuthorTopic') || [];
              const exactMatch = matches.find(t => t.title.toLowerCase() === pattern.commentaryName.toLowerCase());
              if (exactMatch) {
                indexSpecificUpdates.authors = [exactMatch.key];
              } else {
                delete indexSpecificUpdates.authors;
              }
            } catch (e) {
              delete indexSpecificUpdates.authors;
            }
          } else {
            delete indexSpecificUpdates.authors;
          }
        }

        // Validate base_text_titles: every entry must be an existing Index.
        // If any is missing, drop the whole field for this index.
        if (indexSpecificUpdates.base_text_titles?.length) {
          const titles = indexSpecificUpdates.base_text_titles;
          const exists = await Promise.all(titles.map(t => indexExists(t)));
          const missing = titles.filter((t, i) => !exists[i]);
          if (missing.length) {
            delete indexSpecificUpdates.base_text_titles;
            warnings.push(`${indexTitle}: skipped base_text_titles (no such index: ${missing.join(', ')})`);
          }
        }

        // Validate collective_title: a matching Term must exist.
        // If not, drop the field for this index.
        if (indexSpecificUpdates.collective_title) {
          const exists = termExists(indexSpecificUpdates.collective_title);
          if (!exists) {
            warnings.push(`${indexTitle}: skipped collective_title (no term "${indexSpecificUpdates.collective_title}"). If you just created it, reload the page and try again.`);
            delete indexSpecificUpdates.collective_title;
          }
        }

        const postData = {
          title: indexTitle,
          categories: existingIndexData.categories,
          schema: existingIndexData.schema,
          ...indexSpecificUpdates
        };

        const indexPath = encodeURIComponent(indexTitle.replace(/ /g, "_"));

        // Update index via raw API. index_api reads request.POST.get("json"), so the
        // body must be form-encoded (application/x-www-form-urlencoded), not JSON.
        const updateResponse = await fetch(`/api/v2/raw/index/${indexPath}?update=1`, {
          method: 'POST',
          mode: 'same-origin',
          credentials: 'same-origin',
          headers: {
            'X-CSRFToken': getCsrfToken(),
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: new URLSearchParams({ json: JSON.stringify(postData) }).toString()
        });
        const updateData = await updateResponse.json();
        if (!updateResponse.ok || updateData.error) {
          throw new Error(updateData.error || "Failed to update index");
        }

        // Clear caches (non-JSON endpoint)
        const resetResponse = await fetch(`/admin/reset/${encodeURIComponent(indexTitle)}`, {
          method: 'GET',
          credentials: 'same-origin'
        });
        if (!resetResponse.ok) {
          throw new Error("Failed to reset cache");
        }

        successCount++;
      } catch (e) {
        const errorMsg = e.message || 'Unknown error';
        errors.push(`${indexTitle}: ${errorMsg}`);
      } finally {
        processed++;
        setProgress({ current: processed, total: pick.size, label: indexTitle });
      }
    }

    const warningText = warnings.length ? ` Warnings: ${warnings.join('; ')}` : '';
    if (errors.length) {
      setMsg(`Updated ${successCount} of ${pick.size} indices. Errors: ${errors.join('; ')}${warningText}`);
    } else {
      setMsg(`Successfully updated ${successCount} indices.${warningText}`);
      if (!warnings.length) setUpdates({});
    }
    setProgress({ current: 0, total: 0, label: "" });
    setSaving(false);
  };

  /**
   * Render a field input based on its metadata
   */
  const renderField = (fieldName) => {
    const fieldMeta = INDEX_FIELD_METADATA[fieldName];
    const currentValue = updates[fieldName] || "";

    const commonProps = {
      className: "dlVersionSelect fieldInput",
      placeholder: fieldMeta.placeholder,
      value: currentValue,
      onChange: e => handleFieldChange(fieldName, e.target.value),
      style: { width: "100%", direction: fieldMeta.dir || "ltr" }
    };

    return (
      <div key={fieldName} className="fieldGroup">
        <label>{fieldMeta.label}:</label>

        {fieldMeta.help && (
          <div className="fieldHelp">{fieldMeta.help}</div>
        )}

        {fieldMeta.auto && (
          <div className="autoWarning">
            ⚠ <code>'auto'</code> only works for titles using the "X on Y" naming
            pattern (e.g., "Rashi on Genesis"). It is skipped for any selected index
            whose title doesn't match.
          </div>
        )}

        {fieldName === "categories" ? (
          <select {...commonProps}>
            <option value="">Select category...</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        ) : fieldMeta.type === "select" && fieldMeta.options ? (
          <select {...commonProps}>
            {fieldMeta.options.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        ) : fieldMeta.type === "textarea" ? (
          <textarea {...commonProps} rows={3} />
        ) : (
          <input {...commonProps} type="text" />
        )}
      </div>
    );
  };

  // Check if there are actual changes
  const hasChanges = Object.keys(updates).some(k => updates[k]);

  return (
    <ModToolsSection
      title="Bulk Edit Index Metadata"
      titleHe="עריכת אינדקסים בכמות"
      helpContent={HELP_CONTENT}
    >
      {/* Warning box */}
      <div className="warningBox">
        <strong>Important Notes:</strong>
        <ul>
          <li><strong>Authors:</strong> Must exist in the Authors topic. Use exact names or slugs.</li>
          <li><strong>Base Text Titles:</strong> Must be exact index titles (e.g., "Mishnah Berakhot").</li>
          <li><strong>Auto-detection:</strong> Works for commentary texts with "X on Y" format.</li>
        </ul>
      </div>

      {/* Search bar */}
      <div className="searchRow">
        <input
          className="dlVersionSelect"
          type="text"
          placeholder="Version title (e.g., 'Torat Emet 357')"
          value={vtitle}
          onChange={e => setVtitle(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <button
          className="modtoolsButton"
          onClick={load}
          disabled={loading || !vtitle.trim()}
        >
          {loading ? <><span className="loadingSpinner" />Searching...</> : "Find Indices"}
        </button>
      </div>

      {/* Language filter - inline */}
      <div className="filterRow">
        <label>Filter by language:</label>
        <select
          className="dlVersionSelect"
          value={lang}
          onChange={e => setLang(e.target.value)}
        >
          <option value="">All languages</option>
          <option value="he">Hebrew only</option>
          <option value="en">English only</option>
        </select>
      </div>

      {/* Clear button - centered */}
      {searched && (
        <div className="clearSearchRow">
          <button
            className="modtoolsButton secondary"
            onClick={clearSearch}
            type="button"
          >
            Clear Search
          </button>
        </div>
      )}

      {/* No results message */}
      {searched && !loading && indices.length === 0 && (
        <div className="noResults">
          <strong>No indices found with version "{vtitle}"</strong>
          Please verify the exact version title. Version titles are case-sensitive
          and must match exactly (e.g., "Torat Emet 357" not "torat emet").
        </div>
      )}

      {/* Index selector */}
      {indices.length > 0 && (
        <IndexSelector
          indices={indices}
          selectedIndices={pick}
          onSelectionChange={setPick}
          label="indices"
        />
      )}

      {/* Field inputs */}
      {pick.size > 0 && (
        <>
          <div className="subsectionHeading">
            Edit fields for {pick.size} selected {pick.size === 1 ? 'index' : 'indices'}:
          </div>

          <div className="fieldGroupSection">
            {Object.keys(INDEX_FIELD_METADATA).map(f => renderField(f))}
          </div>

          {hasChanges && (
            <div className="changesPreview">
              <strong>Changes to apply:</strong>
              <ul>
                {Object.entries(updates).filter(([k, v]) => v).map(([k, v]) => (
                  <li key={k}>{INDEX_FIELD_METADATA[k]?.label || k}: "{v}"</li>
                ))}
              </ul>
            </div>
          )}

          <div className="actionRow">
            <button
              className="modtoolsButton"
              disabled={!hasChanges || saving}
              onClick={save}
            >
              {saving ? <><span className="loadingSpinner" />Saving...</> : `Save Changes to ${pick.size} Indices`}
            </button>
          </div>

          {saving && progress.total > 0 && (
            <div className="saveProgress">
              <div className="saveProgressHeader">
                <span>{progress.current} of {progress.total} indices</span>
                <span>{Math.round((progress.current / progress.total) * 100)}%</span>
              </div>
              <div className="saveProgressTrack">
                <div
                  className="saveProgressBar"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              {progress.label && (
                <div className="saveProgressLabel">Updating: {progress.label}</div>
              )}
            </div>
          )}
        </>
      )}

      <StatusMessage message={msg} />
    </ModToolsSection>
  );
};

export default BulkIndexEditor;
